import FileIcon from "@mui/icons-material/InsertDriveFile";
import PreviewIcon from "@mui/icons-material/Preview";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Dropzone } from "components/Dropzone";
import { DropzoneErrorDisplay, getUserFriendlyErrorCode } from "components/DropzoneErrorDisplay";
import pluralize from "pluralize";
import { FileRejection } from "react-dropzone";

export type DocumentFilePickerProps = {
  files: File[];
  onDrop: (files: File[]) => void;
  onRemove: (index: number) => void;
  onPreview?: (file: File) => void;
  canAddMore: boolean;
  maxFiles?: number;
  multiple?: boolean;
  disabled?: boolean;
  accept?: string | string[];
  helperText?: string;
  showFileList?: boolean;
  showPreviewButton?: boolean;
  dropzoneHeight?: string | number;
  dropzoneMinWidth?: string | number;
  hasPossibleDuplicates?: boolean;
  dropRejectedFiles?: FileRejection[];
  setDropRejectedFiles?: (files: FileRejection[]) => void;
};

const formatFileSize = (sizeInBytes: number): string => {
  if (sizeInBytes < 1024) {
    return `${sizeInBytes} B`;
  }
  if (sizeInBytes < 1024 * 1024) {
    return `${(sizeInBytes / 1024).toFixed(2)} KB`;
  }
  return `${(sizeInBytes / 1024 / 1024).toFixed(2)} MB`;
};

export function DocumentFilePicker({
  files,
  onDrop,
  onRemove,
  onPreview,
  canAddMore,
  maxFiles = Infinity,
  multiple = false,
  disabled = false,
  accept,
  helperText,
  showFileList = true,
  showPreviewButton = true,
  dropzoneHeight,
  dropzoneMinWidth,
  hasPossibleDuplicates,
  dropRejectedFiles,
  setDropRejectedFiles,
}: DocumentFilePickerProps) {
  const theme = useTheme();

  const dropzoneStyles: Record<string, string | number | undefined> = {
    height: dropzoneHeight ?? theme.spacing(8),
  };
  if (dropzoneMinWidth !== undefined) {
    dropzoneStyles.minWidth = dropzoneMinWidth;
  }

  const handleDrop = (droppedFiles: File[]) => {
    if (!canAddMore) {
      return;
    }
    onDrop(droppedFiles);
  };

  const dropzoneLabel = multiple
    ? "Drag and drop your documents or click to select file(s)"
    : "Drag and drop your document or click to select a file";

  return (
    <Stack spacing={2}>
      {helperText && <Typography color="text.secondary">{helperText}</Typography>}

      {showFileList &&
        (files.length > 0 || (dropRejectedFiles && dropRejectedFiles.length > 0)) && (
          <Stack spacing={2} sx={{ maxHeight: theme.spacing(40), overflow: "auto" }}>
            {files.map((file, index) => (
              <Stack key={`${file.name}-${index}`} direction="row" spacing={1} alignItems="center">
                <ListItem
                  dense
                  sx={{ bgcolor: theme.palette.encampBlue.light, borderRadius: "4px" }}
                  secondaryAction={
                    showPreviewButton && onPreview ? (
                      <IconButton
                        edge="end"
                        aria-label="preview-document"
                        onClick={() => onPreview(file)}
                        color="primary"
                      >
                        <PreviewIcon />
                      </IconButton>
                    ) : undefined
                  }
                >
                  <ListItemIcon sx={{ minWidth: "unset", paddingRight: theme.spacing(2) }}>
                    <FileIcon />
                  </ListItemIcon>
                  <ListItemText primary={file.name} secondary={formatFileSize(file.size)} />
                </ListItem>
                <Tooltip title="Remove file">
                  <IconButton
                    size="medium"
                    aria-label="remove-file"
                    onClick={() => onRemove(index)}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
            {/* show dropzone errors within the scrollable area */}
            <Stack spacing={2} style={{ marginRight: theme.spacing(1) }}>
              {dropRejectedFiles?.map(({ file, errors }: FileRejection, index) => (
                <DropzoneErrorDisplay
                  key={index}
                  file={file}
                  index={index}
                  errors={errors}
                  setDropRejectedFiles={setDropRejectedFiles}
                  dropRejectedFiles={dropRejectedFiles}
                />
              ))}
            </Stack>
          </Stack>
        )}
      {canAddMore && (
        <Dropzone
          label={dropzoneLabel}
          dragActiveText={multiple ? "Drop files here" : "Drop file here"}
          onDrop={handleDrop}
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          sx={dropzoneStyles}
          displayUploadErrors={false}
          onDropRejected={setDropRejectedFiles}
        />
      )}
      {(hasPossibleDuplicates ||
        (dropRejectedFiles && dropRejectedFiles.length > 0) ||
        files.length > 0) && (
        <Stack>
          {hasPossibleDuplicates && (
            <Typography variant="caption" color="warning">
              Possible duplicates detected. Please review selected documents to avoid creating
              redundant records.
            </Typography>
          )}
          {dropRejectedFiles && dropRejectedFiles.length > 0 && (
            <Typography variant="caption" color="error">
              {dropRejectedFiles.length} {pluralize("error", dropRejectedFiles.length)}:{" "}
              {dropRejectedFiles
                .map(({ errors }) =>
                  errors.map((error) => getUserFriendlyErrorCode(error)).join(", ")
                )
                .join(", ")}
              . {dropRejectedFiles.length > 1 ? "These documents" : "This document"} will be unable
              to upload.
            </Typography>
          )}
          {files.length > 0 && multiple && (
            <Typography variant="caption" color="text.secondary">
              {files.length} {pluralize("file", files.length)} selected
              {Number.isFinite(maxFiles) ? ` (max ${maxFiles})` : ""}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}
